import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Button, Paper, Stack, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  CircularProgress, InputAdornment, Divider, Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const API = process.env.REACT_APP_API_LAUNDRY_URL;
const AGENT_API = process.env.REACT_APP_API_BASE_URL; // e.g. .../api/flask
const authHeaders = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } });
const TEAL = "#006D77";
const DRAWER_WIDTH = 280;
const STATUSES = [
  "pending", "confirmed", "pickup_assigned", "pickup_completed",
  "received_at_laundry", "washing", "drying", "ironing",
  "quality_check", "packed", "out_for_delivery", "delivered", "cancelled",
];
const statusLabel = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// ============================================================ Laundry Orders page (route: /laundry-orders)
const LaundryOrders = () => {
  const navigate = useNavigate();
  const community = sessionStorage.getItem("community") || "";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [agents, setAgents] = useState([]);
  const [assignPickup, setAssignPickup] = useState("");
  const [assignDelivery, setAssignDelivery] = useState("");
  const [summary, setSummary] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/dashboard/orders`, { ...authHeaders(), params: { community } })
      .then((r) => setOrders(r.data || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  };

  const loadSummary = () =>
    axios.get(`${API}/dashboard/orders/summary`, { ...authHeaders(), params: { community } })
      .then((r) => setSummary(r.data)).catch(() => setSummary(null));

  useEffect(() => {
    load();
    loadSummary();
    axios.get(`${AGENT_API}/deliveryagent/available`, { params: { role: "delivery" } })
      .then((r) => setAgents(r.data?.data || [])).catch(() => setAgents([]));
    /* eslint-disable-next-line */
  }, []);

  const filtered = orders.filter((o) => {
    if (filter !== "all" && o.status !== filter) return false;
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return o.order_code?.toLowerCase().includes(t) || o.patient_name?.toLowerCase().includes(t) || o.phone?.toLowerCase().includes(t);
  });

  const refresh = () => { load(); loadSummary(); };

  const openDetail = (o) => { setDetail(o); setNewStatus(o.status); setAssignPickup(""); setAssignDelivery(""); };
  const updateStatus = async () => {
    await axios.put(`${API}/dashboard/orders/${detail.id}/status`, { status: newStatus }, authHeaders());
    setDetail(null); refresh();
  };
  const doAssignPickup = async () => {
    await axios.put(`${API}/dashboard/orders/${detail.id}/assign-pickup`, { agent_id: assignPickup }, authHeaders());
    setDetail(null); refresh();
  };
  const doAssignDelivery = async () => {
    await axios.put(`${API}/dashboard/orders/${detail.id}/assign-delivery`, { agent_id: assignDelivery }, authHeaders());
    setDetail(null); refresh();
  };
  const statusColor = (s) => s === "delivered" ? "success" : s === "cancelled" ? "default" : "warning";
  const agentName = (id) => agents.find((a) => a.agentId === id)?.name || id;

  return (
    <Box sx={{
      minHeight: "100vh", bgcolor: "#EEF4F5",
      ml: { xs: 0, md: `${DRAWER_WIDTH}px` }, mt: { xs: "56px", md: 0 },
      px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 },
    }}>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}
          sx={{ mb: 2, textTransform: "none", color: TEAL, fontWeight: 600 }}>Back</Button>
        <Typography variant="h4" fontWeight={800} color={TEAL} gutterBottom
          sx={{ fontSize: { xs: "1.6rem", sm: "2.125rem" } }}>Laundry Orders</Typography>
        <Typography color="text.secondary" mb={3}>
          Track orders, assign pickup and delivery agents, and update status{community ? ` for ${community}` : ""}.
        </Typography>

        {loading ? (
          <Box textAlign="center" py={6}><CircularProgress sx={{ color: TEAL }} /></Box>
        ) : (
          <>
            {summary && (
              <Grid container spacing={2} mb={3}>
                {[
                  { label: "Total Orders", value: summary.total, color: TEAL },
                  { label: "Active", value: summary.active, color: "#F59E0B" },
                  { label: "Delivered", value: summary.delivered, color: "#16A34A" },
                  { label: "Cancelled", value: summary.cancelled, color: "#94A3B8" },
                  { label: "Revenue", value: `₹${(summary.revenue || 0).toLocaleString()}`, color: TEAL },
                ].map((c) => (
                  <Grid item xs={6} sm={4} md={2.4} key={c.label}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: "center" }}>
                      <Typography variant="h5" fontWeight={800} sx={{ color: c.color }}>{c.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}

            <Paper variant="outlined" sx={{ borderRadius: 3, p: { xs: 2, sm: 3 } }}>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap
                justifyContent={{ xs: "flex-start", sm: "flex-end" }} mb={2}>
                <TextField size="small" placeholder="Search code / name / phone" value={q}
                  onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 220 }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                <TextField select size="small" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ minWidth: 160 }}>
                  <MenuItem value="all">All statuses</MenuItem>
                  {STATUSES.map((s) => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
                </TextField>
              </Stack>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: "auto" }}>
                <Table sx={{ minWidth: 760 }}>
                  <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                    <TableRow>
                      <TableCell>Code</TableCell><TableCell>Customer</TableCell><TableCell>Service</TableCell>
                      <TableCell>Pickup</TableCell><TableCell>Total</TableCell><TableCell>Payment</TableCell>
                      <TableCell>Status</TableCell><TableCell align="right">Manage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((o) => (
                      <TableRow key={o.id} hover>
                        <TableCell><Typography fontFamily="monospace" fontWeight={700}>{o.order_code}</Typography></TableCell>
                        <TableCell>{o.patient_name}<br /><Typography variant="caption" color="text.secondary">{o.phone}</Typography></TableCell>
                        <TableCell>{o.service_type}</TableCell>
                        <TableCell>{o.pickup_date}<br /><Typography variant="caption" color="text.secondary">{o.pickup_time}</Typography></TableCell>
                        <TableCell>₹{o.total_amount}</TableCell>
                        <TableCell>
                          <Chip size="small" label={o.payment_status === "paid" ? "Paid" : "Pending"} color={o.payment_status === "paid" ? "success" : "default"} />
                          <Typography variant="caption" display="block" color="text.secondary">{o.payment_method}</Typography>
                        </TableCell>
                        <TableCell><Chip size="small" label={statusLabel(o.status)} color={statusColor(o.status)} /></TableCell>
                        <TableCell align="right"><Button size="small" onClick={() => openDetail(o)} sx={{ textTransform: "none", color: TEAL }}>Manage</Button></TableCell>
                      </TableRow>
                    ))}
                    {filtered.length === 0 && (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ color: "text.secondary", py: 4 }}>No orders found.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </Box>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>Order {detail?.order_code}</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Typography variant="body2"><b>Customer:</b> {detail.patient_name} · {detail.phone}</Typography>
              <Typography variant="body2"><b>Service:</b> {detail.service_type}</Typography>
              <Typography variant="body2"><b>Pickup:</b> {detail.pickup_date} {detail.pickup_time}</Typography>
              <Typography variant="body2"><b>Delivery:</b> {detail.delivery_date} {detail.delivery_time}</Typography>
              <Typography variant="body2"><b>Address:</b> {detail.pickup_address}</Typography>

              <Typography variant="body2" fontWeight={700} mt={2} mb={1}>Items</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                    <TableRow><TableCell>Item</TableCell><TableCell>Qty</TableCell><TableCell>Price</TableCell><TableCell>Total</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.items || []).map((it, i) => (
                      <TableRow key={i}><TableCell>{it.category} · {it.item}</TableCell><TableCell>{it.quantity}</TableCell>
                        <TableCell>₹{it.price}</TableCell><TableCell>₹{it.total}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="body1" fontWeight={800}>Total: ₹{detail.total_amount}</Typography>

              <Divider sx={{ my: 2 }} />

              {/* Assign pickup agent */}
              <Typography variant="body2" fontWeight={700} mb={1}>Pickup Agent</Typography>
              {detail.pickup_agent_id ? (
                <Chip label={`Assigned: ${agentName(detail.pickup_agent_id)}`} color="success" size="small" sx={{ mb: 1 }} />
              ) : (
                <Stack direction="row" spacing={1} mb={1}>
                  <TextField select size="small" label="Choose agent" value={assignPickup}
                    onChange={(e) => setAssignPickup(e.target.value)} sx={{ minWidth: 200 }}>
                    {agents.map((a) => <MenuItem key={a.agentId} value={a.agentId}>{a.name} ({a.phone})</MenuItem>)}
                  </TextField>
                  <Button variant="contained" disabled={!assignPickup} onClick={doAssignPickup}
                    sx={{ bgcolor: TEAL, textTransform: "none" }}>Assign Pickup</Button>
                </Stack>
              )}

              {/* Assign delivery agent */}
              <Typography variant="body2" fontWeight={700} mt={2} mb={1}>Delivery Agent</Typography>
              {detail.delivery_agent_id ? (
                <Chip label={`Assigned: ${agentName(detail.delivery_agent_id)}`} color="success" size="small" sx={{ mb: 1 }} />
              ) : (
                <Stack direction="row" spacing={1} mb={1}>
                  <TextField select size="small" label="Choose agent" value={assignDelivery}
                    onChange={(e) => setAssignDelivery(e.target.value)} sx={{ minWidth: 200 }}>
                    {agents.map((a) => <MenuItem key={a.agentId} value={a.agentId}>{a.name} ({a.phone})</MenuItem>)}
                  </TextField>
                  <Button variant="contained" disabled={!assignDelivery} onClick={doAssignDelivery}
                    sx={{ bgcolor: TEAL, textTransform: "none" }}>Assign Delivery</Button>
                </Stack>
              )}

              <Divider sx={{ my: 2 }} />
              <TextField select fullWidth margin="normal" label="Update status" value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}>
                {STATUSES.map((s) => <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>)}
              </TextField>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDetail(null)} sx={{ textTransform: "none" }}>Close</Button>
          <Button variant="contained" onClick={updateStatus} sx={{ bgcolor: TEAL, textTransform: "none" }}>Update Status</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LaundryOrders;